import {
	Paper, Tooltip, LinearProgress
} from "@mui/material";
import { ReactComponent as IconTable } from "assets/icon_table_th.svg";
import { ReactComponent as NotAnalysisSvg } from "assets/notAnalysis.svg";
import { ReactComponent as MapAnalysisSvg } from "assets/map_analysis.svg";
import Icon from "components/icon/icon";
import { AppAction, AppContext } from "helpers/context";
import { handleFilterLocations } from "helpers/helpers";
import { useContext, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import CustomAccordion from "components/accordion/Accordion";
import styles from "./hazard-multiple-results.module.scss";
import TryAgain from "components/tryAgain/tryAgain";
import BarChart from "components/results/chart-modal/bar-chart/bar-chart"
import ShareTable from "components/results/table-modal/share-table/share-table"
import { handleGetBarChartGroupData } from "helpers/groupBarChartData"
import { handleGetMultipleResultGroupData, countTotalCheckedPointIds } from "helpers/groupResultData"

export default function HazardMultipleResults() {
	const [context, dispatch] = useContext(AppContext);
	const [t] = useTranslation();
	const [count, setCount] = useState<number>(0);
	const [chartOption, setChartOption] = useState<any>(null);
	const [chartHeight, setChartHeight] = useState(0);
	const [activeLegend, setActiveLegend] = useState(0);
	const [defendeActiveLegend, setDefendeActiveLegend] = useState(0);
	const [tableData, setTableData] = useState<any[]>([]);
	const [defendeTableData, setDefendeTableData] = useState<any[]>([]);
	const [noData, setNoData] = useState<any[]>([]);
	const noAnalysisLocationRef = useRef<any[]>([]);
	const [locationsTable, setLocationsTable] = useState<any[]>([])


	const handleLegendClick = (index: number, layerId: string) => {
		setActiveLegend(index);
		let i = defendeTableData.findIndex((i) => i[0].layerId === layerId);
		setDefendeActiveLegend(i)
	};

	const handleSetOptions = (tableData: any, activIndex = activeLegend) => {
		const { Ynum, option } = handleGetBarChartGroupData({
			tableData,
			activIndex,
			noAnalysisLocationRef,
			analysedLegends: context.hazardMultipleAnalysedLegends,
			analysedLocationSet: context.currentMultipleLocationsAnalysed ?? context.currentMultipleLocations,
			t,
		})
		setChartHeight(Ynum);
		setChartOption(option);
	};


	const showInfo = () => {
		dispatch({ type: AppAction.setShowLocationAnalysisDetails, payload: true });
	};

	const handleRetry = () => {
		dispatch({ type: AppAction.setTryAgainType, payload: "analyse" });
	}

	const handleGetTableData = () => {
		if (context.hazardMultipleAnalysisStore
			&& context.hazardMultipleAnalysedLegends
			&& context.hazardMultipleAnalysisStatus !== "error"
			&& context.hazardMultipleAnalysisStatus !== "running"
			&& locationsTable.length
		) {

			const {
				result,
				fathomDefendeResult,
				noData,
				noAnalysis,
				chartData
			} = handleGetMultipleResultGroupData({
				storeData: context.hazardMultipleAnalysisStore,
				locationsTableData: locationsTable,
				legendData: context.hazardMultipleAnalysedLegends
			})
			handleSetOptions(chartData);

			setNoData(noData);
			setTableData(result);
			setDefendeTableData(fathomDefendeResult);
			noAnalysisLocationRef.current = noAnalysis;

		} else {
			setNoData([])
			handleSetOptions([], 0);
			setTableData([]);
			setDefendeTableData([]);
			setActiveLegend(0);
			setDefendeActiveLegend(0);
		}
	}

	useEffect(() => {
		if (!tableData) {
			return;
		};
		const count = countTotalCheckedPointIds(tableData[activeLegend] || [])
		setCount(count);
	}, [tableData]);

	useEffect(() => {
		handleGetTableData()
	}, [locationsTable, context.hazardMultipleAnalysisStore, context.hazardMultipleAnalysedLegends]);

	useEffect(() => {
		setActiveLegend(0);
		setDefendeActiveLegend(0);
	}, [context.hazardMultipleAnalysisStatus])

	useEffect(() => {
		let data = handleFilterLocations({
			data: context.currentMultipleLocations?.items || [],
			unit: context.currentMultipleLocations?.aggregationUnit ?? "numberOfLocations",
			multipleLocationsSearch: context.multipleLocationsSearch,
			locationsFilterInfo: context.locationsFilterInfo,
		})
		setLocationsTable(data)
	}, [context.currentMultipleLocations?.items, context.multipleLocationsSearch, context.locationsFilterInfo, context.currentMultipleLocations?.aggregationUnit])

	if (context.hazardMultipleAnalysisStatus && context.hazardMultipleAnalysisStatus === "error") {
		return (<TryAgain handleRetry={handleRetry} />);
	}

	if (!context.hazardMultipleAnalysedLegends || context.hazardMultipleAnalysedLegends.length === 0) {
		return (
			<div className="h-full flex flex-col items-center justify-center">
				<NotAnalysisSvg className="w-[60px] h-[60px] mx-auto mb-4" />
				<div style={{ color: "var(--gray-text)" }}>{t("single.notMultipleAnalysis")}</div>
			</div>
		);
	}

	if (context.hazardMultipleAnalysisStatus && context.hazardMultipleAnalysisStatus === "running") {
		return (
			<div className="h-full flex flex-col items-center justify-center">
				<LinearProgress
					color="success"
					style={{ width: "40%", marginBottom: "10px" }}
				/>
				<div>{t("multiple.alalyseLoadingContent")}</div>
			</div>
		);
	}

	const randerDetail = () => {
		const layerId = tableData[activeLegend]?.[0]?.layerId
		if (layerId === 'FL_Fluvial_Global_Fathom' ||
			layerId === 'FL_Coastal_Global_Fathom'
		) {
			return <>
				<CustomAccordion title={t("fathom.Undefended")}>
					<ShareTable
						tableData={tableData[activeLegend]}
						activeLegend={activeLegend}
						noAnalysisLocationRef={noAnalysisLocationRef}
						analysedLegends={context.hazardMultipleAnalysedLegends}
						type="hazard"
					/>
				</CustomAccordion>
				<CustomAccordion title={t("fathom.Defended")}>
					<ShareTable
						tableData={defendeTableData[defendeActiveLegend]}
						activeLegend={defendeActiveLegend}
						noAnalysisLocationRef={noAnalysisLocationRef}
						analysedLegends={context.hazardMultipleAnalysedLegends}
						type="hazard-fathom"
					/>
				</CustomAccordion>
			</>
		}
		return <ShareTable
			tableData={tableData[activeLegend]}
			activeLegend={activeLegend}
			noAnalysisLocationRef={noAnalysisLocationRef}
			analysedLegends={context.hazardMultipleAnalysedLegends}
			type="hazard"
		/>
	}

	return (
		<>
			<div className={styles.locationInfo}>
				<div className="flex justify-between items-center">
					<span className="text-[#000000de] text-[13px] font-medium">{t("multiple.overview")}</span>
					<div className="flex items-center">
						<div className="mt-[3px] flex items-center">
							<Tooltip placement="top" title={t("results.resultsFor")}>
								<span>{t("results.resultsFor")}</span>
							</Tooltip>
							<MapAnalysisSvg className="w-[24px] h-[24px] fill-[#0009] mx-[5px] mt-[-5px]" />
							<Tooltip placement="top" title={`${(count) + " / " + locationsTable.filter(v => v.checked).length}${t("results.locations")}`}>
								<span>
									{count}
									{" / "}
									{locationsTable.filter(v => v.checked).length}
									{t("results.locations")}
								</span>
							</Tooltip>
						</div>
						<Icon
							icon="info"
							type="outlined"
							onClick={showInfo}
						></Icon>
					</div>
				</div>
			</div>
			<BarChart
				tableData={tableData}
				activeLegend={activeLegend}
				noAnalysisLocationRef={noAnalysisLocationRef}
				noData={noData}
				chartHeight={chartHeight}
				chartOption={chartOption}
				analysedLegends={context.hazardMultipleAnalysedLegends}
				handleLegendClick={handleLegendClick}
			/>
			{(tableData[activeLegend] || noData.length > 0 || noAnalysisLocationRef.current.length > 0) && (
				<div style={{ marginRight: "0.75rem" }}>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingRight: "10px" }}>
						<p className="m-2 font-bold">{t(`layerId.${tableData[activeLegend] ?
							tableData[activeLegend][0].layerId
							:
							(noData[0] ?
								noData[0].layerId
								:
								context.hazardMultipleAnalysedLegends && context.hazardMultipleAnalysedLegends[activeLegend].layerId)}`)}</p>
						<div style={{ padding: "6px 10px", borderRadius: "5px", backgroundColor: "var(--green-hover)", boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.15)" }}>
							<IconTable />
						</div>
					</div>
					<Paper
						sx={{ width: "100%", boxShadow: "none" }}
						className={styles.table}
					>
						{randerDetail()}
					</Paper>
				</div >
			)
			}
		</>
	);
}

